import type { VideoFormat } from "@/lib/hooks/useImageUpload";

export type IntroStyle = "cinematic" | "architect" | "editorial" | "geometric" | "typewriter";

export interface WatermarkSettings {
  logoEnabled: boolean;
  introEnabled: boolean;
  opacity: number;
  alignment: string;
  padding: number;
  size: number;
  introTitle?: string;
  introStyle?: IntroStyle;
  introAnimation?: string;
  introBgColor?: string;
  introBgOpacity?: number;
  introTextColor?: string;
  introFont?: string;
  introAlignment?: string;

  introFrameUrls?: string[];  // Individual frame PNG URLs for the intro animation
  contactCard?: string;
}

export interface VideoImage {
  id: string;
  src?: string;
  effect: string;
  isTemp?: boolean;
  disabled?: boolean;
  mediaType?: "image" | "video";
}

export const MIN_IMAGES = 6;
export const MAX_IMAGES = 12;
export const INITIAL_SLOTS = 6;

export const getContainerAlignment = (alignment: string): React.CSSProperties => {
  const parts = alignment.split("-");
  const y = parts[0];
  const x = parts.length > 1 ? parts[1] : "center"; // Default to center if only one part (e.g., 'center')
  const style: React.CSSProperties = {};

  if (y === "top") style.alignItems = "flex-start";
  if (y === "center") style.alignItems = "center";
  if (y === "bottom") style.alignItems = "flex-end";

  if (x === "left") style.justifyContent = "flex-start";
  if (x === "center") style.justifyContent = "center";
  if (x === "right") style.justifyContent = "flex-end";

  return style;
};

export const resizeImageFile = (file: File, maxWidth: number): Promise<File> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width <= maxWidth) {
        // No resize needed
        resolve(file);
        return;
      }
      const scale = maxWidth / img.width;
      const canvas = document.createElement("canvas");
      canvas.width = maxWidth;
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: file.type }));
        } else {
          resolve(file);
        }
      }, file.type);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });
};

export const getContrastColor = (hexColor: string) => {
  if (!hexColor || hexColor.length < 7) return "#FFFFFF";
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000000" : "#FFFFFF";
};

export const BRANDING_PLACEHOLDER = `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImJsYWNrIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PHJlY3Qgd2lkdGg9IjE4IiBoZWlnaHQ9IjE4IiB4PSIzIiB5PSIzIiByeD0iMiIgcnk9IjIiLz48Y2lyY2xlIGN4PSI5IiBjeT0iOSIgcj0iMiIvPjxwYXRoIGQ9Im0yMSAxNS0zLjA4Ni0zLjA4NmEyIDIgMCAwIDAtMi44MjggMEw2IDIxIi8+PC9zdmc+`;
